import { ConfirmationDialogRaw } from "@/controls/modals/ConfirmationDialogRaw";
import { HeatLinkButton } from "@/shared/data-entries/HeatLinkButton";
import { NotesFieldDE } from "@/shared/data-entries/common/NotesFieldDE";
import { ProcessDataTextFieldDE } from "@/shared/data-entries/process-data/ProcessDataTextFieldDE";
import { formatProductionDate } from "@/utils/date-time-formatter";
import { ErrorModal, HelmetDexteel } from "@dexteel/mesf-core";
import { Box, Button, Grid, Paper, TextField, Typography } from "@mui/material";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { NotificationSnackBar } from "../../../controls/snackbars/notification-snackbar";
import { RefreshButtons } from "../../../shared/data-entries/RefreshButtons";
import { StatusTitle } from "../../../shared/data-entries/StatusTitle";
import { ProcessDataDateTimeFieldDE } from "../../../shared/data-entries/process-data/ProcessDataDateTimeFieldDE";
import { OperatorSelector } from "../../../shared/selectors/OperatorSelector";
import { ProcessNavigator } from "@/shared/data-entries/ProcessNavigator";
import { searchProcesses } from "../data-entry/repositories/caster.repositories";
import { getLastestProcesses } from "./repositories/torches.repositories";
import { LinkCasterProcess } from "./components/modals/LinkCasterDE";
import { HeatSelector } from "./components/selectors/HeatSelector";
import { ReadOnlyTable } from "./components/tables/table-groupedLength/ReadOnlyTable";
import { useBilletsYardTableData } from "./components/tables/table-groupedLength/useBilletsYardDataTable";
import { useGroupedLengthTableData } from "./components/tables/table-groupedLength/useGroupedLengthDataTable";
import { TableTorches } from "./components/tables/table-torches/TableTorches";
import { useDataEntryContext } from "./context/reducer.provider";
import { useTimerContext } from "./context/timer.provider";
import { useLinkUnlinkProcess } from "./hooks/useLinkUnlinkProcess";
import { useTorchesDataEntryActions } from "./hooks/useTorchesDataEntryActions";
import { Process } from "./models/torches.models";

export const TorchesDataEntryPage = () => {
  const navigate = useNavigate();
  const params = useParams();
  const { seconds, restart, isRunning, pause, resume } = useTimerContext();

  const {
    state: { deProcess, notificationMessage, errorMessage, isDataLoading },
    actions: { setProcess, setNotificationMessage, setErrorMessage },
  } = useDataEntryContext();
  const { saveProcessData, refreshData, loadData } =
    useTorchesDataEntryActions();

  const [showArrive, setShowArrive] = useState(false);

  const process = deProcess?.process;

  const { columns: useGroupedLengthData } = useGroupedLengthTableData();
  const { columns: useBilletsYardData } = useBilletsYardTableData();

  const {
    handleLinkHeat,
    handleUnlinkHeat,
    showLinkModal,
    showUnlinkModal,
    unlinkProcess,
    setShowLinkModal,
  } = useLinkUnlinkProcess(process || ({} as Process));

  return (
    <>
      <HelmetDexteel
        title={`${
          process?.HeatName ? `${process.HeatName} - ` : ""
        }Torches Data Entry`}
      />
      <Box padding={2} style={{ height: "calc(100vh - 64px)" }}>
        <Grid container spacing={1} direction={"column"}>
          <Grid item>
            <Typography variant={"h5"} sx={{ fontWeight: 600 }}>
              Torches Data Entry
            </Typography>
          </Grid>
          <Grid item>
            <Paper>
              <Box padding={1}>
                <Grid
                  container
                  alignItems={"flex-start"}
                  justifyContent="space-between"
                  spacing={3}
                >
                  <Grid item xs={12} md={2}>
                    <Grid container direction="column" spacing={1}>
                      <Grid item>
                        <ProcessNavigator
                          key={`torches-de-process-navigator`}
                          process={deProcess?.process ?? null}
                          loadData={loadData}
                          getLatestProcesses={getLastestProcesses}
                          searchProcesses={searchProcesses}
                          pause={pause}
                          resume={resume}
                          onError={setErrorMessage}
                          initialProcessId={
                            params.processId
                              ? Number(params.processId)
                              : undefined
                          }
                        />
                      </Grid>
                      <Grid item>
                        <Grid
                          container
                          style={{ display: "flex", alignItems: "center" }}
                        >
                          <Grid item md={10}>
                            <StatusTitle
                              heatStatus={process?.HeatStatusCode ?? ""}
                            />
                          </Grid>
                          <Grid item md={2}>
                            <HeatLinkButton
                              isLinked={process?.HeatId != null}
                              disabled={!process}
                              onLink={() => {
                                pause();
                                if (process) {
                                  handleLinkHeat();
                                }
                              }}
                              onUnlink={async () => {
                                pause();
                                if (process) {
                                  await unlinkProcess();
                                }
                              }}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    key={`caster-torches-data-entry-first-${process?.ProcessId}`}
                    item
                    xs={12}
                    sm={6}
                    md={2}
                  >
                    <Grid container direction={"column"} spacing={1}>
                      <Grid item>
                        <TextField
                          disabled
                          fullWidth
                          label={"Shift"}
                          variant={"outlined"}
                          value={`${formatProductionDate(process?.ProductionDate)} ${process?.Shift} - ${process?.Crew}`}
                        />
                      </Grid>
                      <Grid item>
                        <ProcessDataDateTimeFieldDE<Process>
                          value={process?.Arrive}
                          field={"Arrive"}
                          label="Arrive"
                          format="MM-DD-YYYY HH:mm"
                          disabled
                          onChange={(value) => {
                            setProcess({
                              ...process,
                              ["Arrive"]: value,
                            });
                          }}
                          onBlur={() => {
                            saveProcessData({
                              processId: process?.ProcessId!,
                              field: "Arrive",
                              value: process?.Arrive ?? null,
                            });
                            resume();
                          }}
                          onFocus={pause}
                        />
                      </Grid>
                      <Grid item>
                        <ProcessDataDateTimeFieldDE<Process>
                          value={process?.LadleOpen}
                          field={"LadleOpen"}
                          label="Ladle Open"
                          format="MM-DD-YYYY HH:mm"
                          disabled
                          onChange={(value) => {
                            setProcess({
                              ...process,
                              ["LadleOpen"]: value,
                            });
                          }}
                          onBlur={() => {
                            saveProcessData({
                              processId: process?.ProcessId!,
                              field: "LadleOpen",
                              value: process?.LadleOpen ?? null,
                            });
                            resume();
                          }}
                          onFocus={pause}
                        />
                      </Grid>
                      <Grid item>
                        <ProcessDataDateTimeFieldDE<Process>
                          value={process?.LadleClose}
                          field={"LadleClose"}
                          label="Ladle Close"
                          format="MM-DD-YYYY HH:mm"
                          disabled
                          onChange={(value) => {
                            setProcess({
                              ...process,
                              ["LadleClose"]: value,
                            });
                          }}
                          onBlur={() => {
                            saveProcessData({
                              processId: process?.ProcessId!,
                              field: "LadleClose",
                              value: process?.LadleClose ?? null,
                            });
                            resume();
                          }}
                          onFocus={pause}
                        />
                      </Grid>
                    </Grid>
                  </Grid>

                  <Grid
                    key={`caster-torches-data-entry-sec-${process?.ProcessId}`}
                    item
                    xs={12}
                    sm={6}
                    md={2}
                  >
                    <Grid container direction={"column"} spacing={1}>
                      <Grid item>
                        <Grid container spacing={1} alignItems="center">
                          <Grid item xs={6} md={6}>
                            <TextField
                              disabled
                              label="Grade"
                              variant="outlined"
                              fullWidth
                              value={process?.GradeName || null}
                            />
                          </Grid>
                          <Grid item xs={6} md={6}>
                            <TextField
                              disabled
                              label="Ladle"
                              variant="outlined"
                              fullWidth
                              value={process?.LadleName || null}
                            />
                          </Grid>
                        </Grid>
                      </Grid>
                      <Grid item>
                        <OperatorSelector
                          disabled={process?.HeatId ? false : true}
                          value={process?.TorchesOperatorForemanId || null}
                          assetId={process?.AssetId ?? 0}
                          onChange={(value: number | null) => {
                            saveProcessData({
                              processId: process?.ProcessId
                                ? process?.ProcessId
                                : 0,
                              field: "TorchesOperatorForemanId",
                              value,
                            });
                          }}
                          label={"Foreman"}
                          onlyForemans={true}
                          onFocus={pause}
                          onBlur={resume}
                        />
                      </Grid>
                      <Grid item>
                        <ProcessDataTextFieldDE<Process>
                          field={"TorchesForemanFillInName"}
                          label={"Fill In"}
                          value={process?.TorchesForemanFillInName}
                          onChange={(value) => {
                            setProcess({
                              ...process,
                              ["TorchesForemanFillInName"]: value,
                            });
                          }}
                          onBlur={() => {
                            saveProcessData({
                              processId: process?.ProcessId!,
                              field: "TorchesForemanFillInName",
                              value: process!["TorchesForemanFillInName"],
                            }).then(() => {
                              resume();
                            });
                          }}
                          onFocus={pause}
                        />
                      </Grid>
                      <Grid item>
                        <OperatorSelector
                          disabled={process?.HeatId ? false : true}
                          value={process?.TorchesOperatorId || null}
                          assetId={process?.AssetId ?? 0}
                          onChange={(value: number | null) => {
                            saveProcessData({
                              processId: process?.ProcessId
                                ? process?.ProcessId
                                : 0,
                              field: "TorchesOperatorId",
                              value,
                            });
                          }}
                          label={"Operator"}
                          onFocus={pause}
                          onBlur={resume}
                        />
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid
                    key={`caster-torches-data-entry-notes-${process?.ProcessId}`}
                    item
                    xs={12}
                    sm={6}
                    md={2}
                    sx={{ mt: 0.5 }}
                  >
                    <NotesFieldDE
                      value={process?.TorchesComments ?? ""}
                      onChange={(value) => {
                        setProcess({
                          ...process,
                          TorchesComments: value,
                        });
                      }}
                      onBlur={() => {
                        saveProcessData({
                          processId: process?.ProcessId!,
                          field: "TorchesComments",
                          value: process?.TorchesComments ?? "",
                        }).then(() => {
                          resume();
                        });
                      }}
                      onFocus={pause}
                      minRows={12}
                      maxRows={12}
                      height="216px"
                      maxHeight="216px"
                      resize="none"
                    />
                  </Grid>
                  <Grid
                    key={`caster-torches-data-entry-third-${process?.ProcessId}`}
                    item
                    xs={12}
                    sm={6}
                    md={2}
                  >
                    <Grid item container direction={"column"} spacing={2}>
                      <Grid item xs={12} sx={{ mt: 0.5 }}>
                        <RefreshButtons
                          seconds={seconds}
                          isRunning={isRunning}
                          pause={pause}
                          restart={restart}
                          resume={resume}
                          refreshData={refreshData}
                          isDataLoading={isDataLoading}
                          process={process}
                        />
                      </Grid>
                      <Grid item xs={12} sx={{ mt: 0.5 }}>
                        <Button
                          fullWidth
                          variant={"outlined"}
                          size="medium"
                          onClick={() =>
                            navigate(
                              `/caster-processes/${process?.ShiftId}?processId=${process?.ProcessId}`,
                            )
                          }
                          sx={{ maxHeight: 35, height: 35 }}
                        >
                          Processes
                        </Button>
                      </Grid>
                      <Grid item xs={12} sm={12} md={12} sx={{ mt: 0.5 }}>
                        <Button
                          fullWidth
                          variant={"outlined"}
                          size="medium"
                          onClick={() =>
                            navigate(`/caster-data-entry/${process?.ProcessId}`)
                          }
                          sx={{ maxHeight: 35, height: 35 }}
                        >
                          Caster Data Entry
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                </Grid>
              </Box>
            </Paper>
          </Grid>

          <Grid item xs={12} md={12}>
            <TableTorches />
          </Grid>

          <Grid item container spacing={1} direction={"row"}>
            <Grid item xs={12} md={6}>
              <ReadOnlyTable
                data={deProcess?.groupedLength ?? []}
                columns={useGroupedLengthData}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <ReadOnlyTable
                data={deProcess?.groupedBilletsYard ?? []}
                columns={useBilletsYardData}
              />
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <HeatSelector
        show={showArrive}
        onHide={() => {
          setShowArrive(false);
          resume();
        }}
      />
      <ConfirmationDialogRaw
        title="Confirmation"
        message={showUnlinkModal.message}
        open={showUnlinkModal.show}
        onClose={handleUnlinkHeat}
      />
      <LinkCasterProcess
        show={showLinkModal}
        onHide={() => {
          setShowLinkModal(false);
          resume();
        }}
      />
      <NotificationSnackBar
        message={notificationMessage}
        onHide={() => setNotificationMessage("")}
      />
      <ErrorModal
        error={errorMessage}
        onHide={() => setErrorMessage("")}
        title="Unexpected error"
      />
    </>
  );
};

export default TorchesDataEntryPage;
